import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Modal,
  Form,
  Input,
  Radio,
  Select,
  Tag,
  Divider,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { makeGetCall, makePostCall, makePutCall, makeDeleteCall } from '../../utils/helper';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { TextArea } = Input;
const { Option } = Select;

// Helper function to decode HTML entities
const decodeHtmlEntities = (html) => {
  if (!html) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const ManageQuestions = ({ quiz, onBack, onViewScores }) => {
  const [questions, setQuestions] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form] = Form.useForm();
  const [explanation, setExplanation] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchQuestions();
    fetchStudies();
  }, [quiz]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await makeGetCall(`/quiz/${quiz.quiz_id}/questions`);
      setQuestions(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch questions');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchStudies = async () => {
    try {
      const response = await makeGetCall('/quiz/studies');
      setStudies(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch studies');
      console.error(error);
    }
  };

  const handleCreateQuestion = () => {
    setEditingQuestion(null);
    form.resetFields();
    form.setFieldsValue({
      options: [
        { id: 'A', text: '' },
        { id: 'B', text: '' }
      ]
    });
    setExplanation('');
    setIsModalVisible(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    form.setFieldsValue({
      studyUID: question.question_study_uid,
      questionText: question.question_text,
      options: question.question_options,
      correctOption: question.question_correct_option
    });
    // Decode HTML entities for proper display in ReactQuill
    const decodedExplanation = decodeHtmlEntities(question.question_explanation);
    setExplanation(decodedExplanation);
    setIsModalVisible(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      await makeDeleteCall(`/quiz/question/${questionId}`);
      message.success('Question deleted successfully');
      fetchQuestions();
    } catch (error) {
      message.error('Failed to delete question');
      console.error(error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        studyUID: values.studyUID,
        questionText: values.questionText,
        options: values.options,
        correctOption: values.correctOption,
        explanation: explanation
      };

      if (editingQuestion) {
        await makePutCall(`/quiz/question/${editingQuestion.question_id}`, payload);
        message.success('Question updated successfully');
      } else {
        await makePostCall(`/quiz/${quiz.quiz_id}/question`, payload);
        message.success('Question created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      setExplanation('');
      fetchQuestions();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || 'Failed to save question');
      console.error(error);
    }
  };

  const filteredStudies = studies.filter(study =>
    study.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Question',
      dataIndex: 'question_text',
      key: 'question_text',
      width: '40%',
      ellipsis: true,
    },
    {
      title: 'Study',
      dataIndex: 'question_study_uid',
      key: 'question_study_uid',
      width: '15%',
      ellipsis: true,
      render: (uid) => {
        const study = studies.find(s => s.studyUID === uid);
        return study ? study.patientName : uid.substring(0, 20) + '...';
      }
    },
    {
      title: 'Options',
      key: 'options',
      width: '10%',
      align: 'center',
      render: (_, record) => (
        <div>
          <Tag color="blue">{record.question_options?.length || 0} options</Tag>
          <Tag color="green">
            {Array.isArray(record.question_correct_option)
              ? record.question_correct_option.length
              : 1} correct
          </Tag>
        </div>
      )
    },
    {
      title: 'Responses',
      key: 'responses',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <div>
          <div>Total: {record.responseCount || 0}</div>
          <div className="text-green-600">
            Correct: {record.correctCount || 0}
            {record.responseCount > 0 &&
              ` (${Math.round((record.correctCount / record.responseCount) * 100)}%)`
            }
          </div>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditQuestion(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure? This will delete all user responses for this question."
            onConfirm={() => handleDeleteQuestion(record.question_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} className="mb-2">
          Back to Quizzes
        </Button>
        <h2 className="text-2xl font-bold">{quiz.quiz_name} - Questions</h2>
        <p className="text-gray-500">Manage questions for this quiz</p>
      </div>

      <div className="mb-4 flex gap-2">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateQuestion}>
          Add Question
        </Button>
        <Button onClick={() => onViewScores(quiz)}>
          View Scores
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={questions}
        loading={loading}
        rowKey="question_id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomCenter']
        }}
      />

      <Modal
        title={editingQuestion ? 'Edit Question' : 'Create New Question'}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setExplanation('');
        }}
        width={900}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="studyUID"
            label="Select Study"
            rules={[{ required: true, message: 'Please select a study' }]}
          >
            <Select
              showSearch
              placeholder="Search by patient name or body part"
              onSearch={setSearchText}
              filterOption={false}
            >
              {filteredStudies.map((study, idx) => (
                <Option
                  key={idx}
                  value={study.studyUID}
                  disabled={study.isAlreadyMapped && !editingQuestion}
                >
                  <div>
                    <div>{study.label}</div>
                    {study.isAlreadyMapped && !editingQuestion && (
                      <Tag color="orange" style={{ fontSize: '10px' }}>Already Mapped</Tag>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="questionText"
            label="Question Text"
            rules={[{ required: true, message: 'Please enter question text' }]}
          >
            <TextArea
              rows={3}
              placeholder="Based on the imaging findings, what is the most likely diagnosis?"
            />
          </Form.Item>

          <Divider orientation="left">Options</Divider>

          <Form.List name="options" initialValue={[{ id: 'A', text: '' }, { id: 'B', text: '' }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16, gap: 8 }}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'id']}
                      style={{ width: 60, marginBottom: 0 }}
                    >
                      <Input placeholder="A" disabled />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'text']}
                      rules={[{ required: true, message: 'Option text required' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <TextArea rows={5} placeholder="Enter detailed option text (can be several sentences)" />
                    </Form.Item>
                    {fields.length > 2 && (
                      <MinusCircleOutlined
                        onClick={() => remove(field.name)}
                        style={{ marginTop: 8, fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
                      />
                    )}
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add({ id: String.fromCharCode(65 + fields.length), text: '' })}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Option
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.options !== currentValues.options}
          >
            {({ getFieldValue }) => {
              const options = getFieldValue('options') || [];
              return (
                <Form.Item
                  name="correctOption"
                  label="Correct Answer(s) - Select one or more"
                  rules={[{
                    required: true,
                    message: 'Please select at least one correct answer',
                    type: 'array',
                    min: 1
                  }]}
                >
                  <Checkbox.Group>
                    <Space direction="vertical">
                      {options.map((opt, idx) => (
                        <Checkbox key={idx} value={opt?.id}>Option {opt?.id}</Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Divider orientation="left">Explanation (Rich Text)</Divider>

          <Form.Item label="Explanation (shown after answer submission)">
            <ReactQuill
              theme="snow"
              value={explanation}
              onChange={setExplanation}
              modules={quillModules}
              placeholder="Explain the correct answer with supporting details and references..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageQuestions;
